/**
 * ListCard Component
 *
 * Card rendering for the lists screen. Shows list name, item count,
 * type badge for default lists, and a cover image or colored placeholder.
 */

import { AppImage } from '@/components/ui/app-image';
import { Text } from '@/components/ui/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import type { List } from '../types';

interface ListCardProps {
  list: List;
  onPress: () => void;
  showOwner?: boolean;
}

const DEFAULT_LIST_ICONS: Record<string, string> = {
  'Recommended to Me': 'thumbs-up',
  'Want to Try': 'flame',
  'Flavrites': 'heart',
};

export function ListCard({ list, onPress, showOwner }: ListCardProps) {
  const defaultIcon = DEFAULT_LIST_ICONS[list.name] ?? 'list-outline';

  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center p-3 rounded-xl bg-card mb-2 border border-border"
      accessibilityRole="button"
      accessibilityLabel={`${list.name}, ${list.itemCount} items`}
    >
      {/* Cover / Placeholder */}
      {list.coverImageUrl ? (
        <View className="rounded-lg overflow-hidden bg-surface-secondary">
          <AppImage
            r2Key={list.coverImageUrl}
            preset="thumbnail"
            style={{ width: 56, height: 56 }}
            accessibilityLabel={`${list.name} cover`}
          />
        </View>
      ) : (
        <View
          className="rounded-lg items-center justify-center bg-surface-secondary"
          style={{ width: 56, height: 56 }}
        >
          <Ionicons
            name={list.type === 'default' ? (defaultIcon as any) : 'list-outline'}
            size={24}
            className="text-muted-foreground"
          />
        </View>
      )}

      {/* Content */}
      <View className="flex-1 ml-3">
        <View className="flex-row items-center">
          <Text
            className="font-semibold text-base text-foreground"
            numberOfLines={1}
            style={{ flexShrink: 1 }}
          >
            {list.name}
          </Text>
        </View>
        {list.description && (
          <Text className="text-sm mt-0.5 text-muted-foreground" numberOfLines={1}>
            {list.description}
          </Text>
        )}
        <Text className="text-xs mt-0.5 text-muted-foreground">
          {list.itemCount} {list.itemCount === 1 ? 'item' : 'items'}
          {list.memberCount > 1 && ` \u00B7 ${list.memberCount} members`}
        </Text>
        {showOwner && list.ownerName && (
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              router.push(`/(root)/profile/${list.ownerId}`);
            }}
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
            accessibilityRole="link"
            accessibilityLabel={`View ${list.ownerName}'s profile`}
          >
            <Text className="text-xs mt-0.5 text-muted-foreground">
              by <Text className="text-primary">{list.ownerName}</Text>
            </Text>
          </Pressable>
        )}
      </View>

      <Ionicons name="chevron-forward" size={18} className="text-muted-foreground ml-2" />
    </Pressable>
  );
}
